import type { CSSProperties } from 'react';
import type { Edge, EdgeMarker, Node } from '@xyflow/react';
import {
  WORKFLOW_EDGE_MARKER,
  WORKFLOW_EDGE_STYLE,
} from './workflow-edge-style';
import type { WorkflowRunFlowNodeData } from './workflow-run-flow-node';

/**
 * An arrow is painted by the step it leaves, because that is the step whose
 * output it carries. Its target hasn't done anything yet.
 *
 * Solid means one thing only — that step is done. Everything short of that is
 * dashed, so the distinction rides on the *shape* of the line rather than its
 * colour: a queued leg and a finished one can't be told apart at a glance if
 * both are solid and only the tint differs.
 */
export type WorkflowRunEdgePhase = 'settled' | 'in-flight' | 'pending';

const RUN_EDGE_COLOR = 'var(--primary)';

const RUN_EDGE_DASH = '6 6';

/** Data has been through here: solid, and tinted so the path taken stands out. */
const SETTLED_EDGE_STYLE: CSSProperties = {
  ...WORKFLOW_EDGE_STYLE,
  stroke: RUN_EDGE_COLOR,
};

/** Still moving: the same tint, dashed, with the dashes marching (`animated`). */
const IN_FLIGHT_EDGE_STYLE: CSSProperties = {
  ...SETTLED_EDGE_STYLE,
  strokeDasharray: RUN_EDGE_DASH,
};

/**
 * Not yet, or never. Dashed like the in-flight leg but in the resting colour and
 * standing still — the dash says "nothing has settled here", and the absence of
 * tint and motion says nothing is happening either.
 */
const PENDING_EDGE_STYLE: CSSProperties = {
  ...WORKFLOW_EDGE_STYLE,
  strokeDasharray: RUN_EDGE_DASH,
};

/**
 * Arrowheads are SVG markers rendered once into a shared `<defs>` and keyed by
 * every field including `color`, so a tinted arrow needs its own — restyling the
 * default one in CSS is not possible. See `withSelectedEdgeHighlight`.
 */
const RUN_EDGE_MARKER: EdgeMarker = {
  ...WORKFLOW_EDGE_MARKER,
  color: RUN_EDGE_COLOR,
};

/**
 * Which phase a step's outgoing arrows are in.
 *
 * `completed` settles the arrow and `running` puts it in flight. Everything else
 * — queued, failed, cancelled, stopped, or a step with no status yet — is
 * pending, which is the honest answer for all of them: nothing has travelled
 * this way. That is what makes a failed run readable at a glance, since the
 * solid arrows stop exactly where the run did.
 */
export function runEdgePhaseForStatus(
  status: string | null | undefined
): WorkflowRunEdgePhase {
  if (status === 'completed') {
    return 'settled';
  }
  if (status === 'running') {
    return 'in-flight';
  }
  return 'pending';
}

/** Run status of each flow node, keyed by flow id — the source lookup below. */
export function buildRunStatusByFlowId(
  nodes: readonly Node[]
): ReadonlyMap<string, string | null> {
  return new Map(
    nodes.map((node) => {
      const data = node.data as WorkflowRunFlowNodeData | undefined;
      return [node.id, data?.status ?? null] as const;
    })
  );
}

const PHASE_STYLES: Record<
  WorkflowRunEdgePhase,
  { style: CSSProperties; animated: boolean; tinted: boolean }
> = {
  settled: { style: SETTLED_EDGE_STYLE, animated: false, tinted: true },
  'in-flight': { style: IN_FLIGHT_EDGE_STYLE, animated: true, tinted: true },
  pending: { style: PENDING_EDGE_STYLE, animated: false, tinted: false },
};

/**
 * Paints each arrow from the state of the step it leaves.
 *
 * Replaces an all-or-nothing "the run is going, so dash everything" pass, which
 * said the same thing about a leg that finished a minute ago and one that hasn't
 * started. Per-edge, the graph reads as progress: solid behind the front,
 * marching at it, dashed and still ahead of it.
 *
 * Render-time only, like `withSelectedEdgeHighlight` — never written back into
 * edge state, or a step finishing would leave its arrow stuck mid-flight.
 */
export function withRunProgressEdges(
  edges: readonly Edge[],
  statusByFlowId: ReadonlyMap<string, string | null>
): Edge[] {
  return edges.map((edge) => {
    const phase = runEdgePhaseForStatus(statusByFlowId.get(edge.source));
    const { style, animated, tinted } = PHASE_STYLES[phase];
    return {
      ...edge,
      animated,
      style,
      // Only a tinted line needs its own arrowhead; a pending one keeps the
      // default marker, which is already the resting colour.
      ...(tinted ? { markerEnd: RUN_EDGE_MARKER } : {}),
    };
  });
}
